import { gunzipSync, gzipSync } from 'fflate';

import { AudioFeatureType } from '../api/cc-smarts/models/AudioFeature';
import type { ContentDetails } from '../types/activeContent';
import { AudioSharingVersion } from '../types/audioSharingVersion';

type ParsedAudioShareData = {
	feature: string;
	type: string;
	payload: string;
};

const generateAudioSharePayload = (
	feature: AudioFeatureType,
	supportingContent: ContentDetails[],
): string => {
	if (feature === AudioFeatureType.BRIEFING) {
		return supportingContent.map((content) => `${content.ari},${content.link}`).join(',');
	}
	return '';
};

const generateCompressedAndEncodedAudioShareValue = (
	feature: AudioFeatureType,
	supportingContent: ContentDetails[],
): string => {
	const payload = generateAudioSharePayload(feature, supportingContent);
	const audioShareValue = `feature=${feature};type=${AudioSharingVersion.V1};payload=${payload}`;
	const compressedAudioShareValue = gzipSync(new TextEncoder().encode(audioShareValue));
	return Buffer.from(compressedAudioShareValue).toString('base64');
};

export const generateAudioShareLink = (
	mainSourceUrl: string,
	feature: AudioFeatureType,
	supportingContent: ContentDetails[],
): string => {
	const url = new URL(mainSourceUrl);
	const audioShareValue = generateCompressedAndEncodedAudioShareValue(feature, supportingContent);
	url.searchParams.set('audioShare', audioShareValue);
	return url.toString();
};

const parseAudioSharePayload = (payload: string): ContentDetails[] => {
	const combinedSources = payload.split(',').filter(Boolean);
	if (combinedSources.length % 2 !== 0) {
		return [];
	}

	const supportingContent: ContentDetails[] = [];
	for (let i = 0; i < combinedSources.length; i += 2) {
		supportingContent.push({
			ari: combinedSources[i],
			link: combinedSources[i + 1],
		});
	}
	return supportingContent;
};

const decompressAudioShareValue = (encodedValue: string): ParsedAudioShareData | null => {
	try {
		const compressedArray = Uint8Array.from(Buffer.from(encodedValue, 'base64'));
		const decompressedArray = gunzipSync(compressedArray);
		const audioShareValue = new TextDecoder().decode(decompressedArray);

		const entries = audioShareValue.split(';').map((part) => part.split('='));
		const data = Object.fromEntries(entries) as Record<string, string>;

		return {
			feature: data.feature || '',
			type: data.type || '',
			payload: data.payload || '',
		};
	} catch {
		return null;
	}
};

export const parseAudioShareLink = (
	audioShareLink: string,
): { feature: AudioFeatureType; supportingContent: ContentDetails[] } => {
	const defaultResult = {
		feature: AudioFeatureType.BRIEFING,
		supportingContent: [],
	};

	try {
		const url = new URL(audioShareLink);
		const encodedValue = url.searchParams.get('audioShare');
		if (!encodedValue) {
			return defaultResult;
		}

		const parsedData = decompressAudioShareValue(encodedValue);
		if (!parsedData) {
			return defaultResult;
		}

		if (parsedData.type !== AudioSharingVersion.V1) {
			return {
				feature: (parsedData.feature as AudioFeatureType) || AudioFeatureType.BRIEFING,
				supportingContent: [],
			};
		}

		return {
			feature: (parsedData.feature as AudioFeatureType) || AudioFeatureType.BRIEFING,
			supportingContent: parseAudioSharePayload(parsedData.payload),
		};
	} catch {
		return defaultResult;
	}
};
